import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatSelectionListChange, MatSelect } from '@angular/material';
import { PartPackageService, EquipmentModelService, BrandService } from '@app/api/api';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

export interface EquipmentModel {
  id: string | number;
  name: string;
  model_code?: string;
}

@Component({
  selector: 'meu-modal-part-package',
  templateUrl: './modal-part-package.component.html',
  styleUrls: ['./modal-part-package.component.scss'],
})
export class ModalPartPackageComponent implements OnInit, OnDestroy {
  isEdit: boolean = false;
  hasFormErrors: boolean = false;
  lstModels: EquipmentModel[] = [];
  lstPrefixes: any[] = [];
  lstBrands: any[] = [];
  host: string = environment.backendhost;

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  currentImage: string | null = null;
  isUploading: boolean = false;
  selectedPrefixIds: string[] = [];

  // Model infinite scroll + brand filter state
  modelSearchCtrl = new FormControl('');
  modelKeyUp = new Subject<string>();
  modelPage = 1;
  modelTotalPages = 1;
  modelLoading = false;
  modelPageSize = 10;
  selectedBrandId: string | number = '';

  @ViewChild('modelSelect', { static: false }) modelSelect!: MatSelect;
  onModelScrollBound = (event: any) => this.onModelScroll(event);

  packageForm = new FormGroup({
    equipment_model_id: new FormControl('', [Validators.required]),
    prefix_ids: new FormControl([]),
    brand_id: new FormControl(''),
    name: new FormControl('', [Validators.required, Validators.pattern('^(?!\\s*$).+')]),
    description: new FormControl(''),
    serial: new FormControl(''),
    is_active: new FormControl(true),
    sort_order: new FormControl(0),
    default_image: new FormControl(''),
  });

  constructor(
    public dialogRef: MatDialogRef<ModalPartPackageComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { id: string | number | null },
    private partPackageService: PartPackageService,
    private equipmentModelService: EquipmentModelService,
    private brandService: BrandService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    const modelIdCtrl = this.packageForm.get('equipment_model_id');
    if (modelIdCtrl) {
      modelIdCtrl.valueChanges.subscribe((modelId) => {
        this.packageForm.patchValue({ prefix_ids: [] }, { emitEvent: false });
        this.selectedPrefixIds = [];
        this.loadPrefixesByModel(modelId);
      });
    }

    const brandIdCtrl = this.packageForm.get('brand_id');
    if (brandIdCtrl) {
      brandIdCtrl.valueChanges.subscribe((brandId) => {
        // Tránh load lại khi patchValue trong lúc edit (xử lý riêng trong getById)
        if (this.isEdit && this.selectedBrandId === '' && brandId) {
          this.selectedBrandId = brandId;
          return;
        }
        if (this.selectedBrandId === brandId) {
          return;
        }
        this.selectedBrandId = brandId || '';
        // Reset model đã chọn khi đổi hãng
        this.packageForm.patchValue({ equipment_model_id: '' }, { emitEvent: false });
        this.selectedPrefixIds = [];
        this.lstPrefixes = [];
        this.loadModels();
      });
    }

    this.modelKeyUp.pipe(debounceTime(300)).subscribe((value) => {
      this.loadModels(value);
    });

    this.loadModels('', () => {
      if (this.data.id != null) {
        this.isEdit = true;
        this.getById(this.data.id);
      }
    });
    this.loadBrands();
  }

  ngOnDestroy() {
    this.modelKeyUp.complete();
  }

  trackById(index: number, item: any): any {
    return item ? item.id : undefined;
  }

  loadBrands() {
    this.brandService.apiBrandsGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstBrands = res.data.collection || [];
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadPrefixesByModel(modelId?: string | number, selectedIds?: string[]) {
    if (!modelId) {
      this.lstPrefixes = [];
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.equipmentModelService.apiEquipmentModelsIdGet(String(modelId)).subscribe((res) => {
      if (res.success && res.data) {
        this.lstPrefixes = res.data.prefixes || [];
        if (selectedIds && selectedIds.length > 0) {
          const validIds = selectedIds.filter((id) =>
            this.lstPrefixes.some((p) => p.id === id)
          );
          this.packageForm.patchValue({ prefix_ids: validIds }, { emitEvent: false });
          this.selectedPrefixIds = validIds.slice();
        }
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  onPrefixSelectionChange(event: MatSelectionListChange) {
    const selectedIds = event.source.selectedOptions.selected.map((opt) => opt.value);
    this.packageForm.patchValue({ prefix_ids: selectedIds });
  }

  loadModels(search?: string, callback?: () => void) {
    this.modelLoading = true;
    this.modelPage = 1;
    this.equipmentModelService
      .apiEquipmentModelsByBrandGet(this.selectedBrandId, search, this.modelPage, this.modelPageSize)
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.modelTotalPages = Math.ceil(res.data.total / this.modelPageSize);

            const modelIdCtrl = this.packageForm.get('equipment_model_id');
            const selectedId = modelIdCtrl ? modelIdCtrl.value : null;
            const selectedItem = selectedId ? this.lstModels.find((m) => m.id === selectedId) : null;

            this.lstModels = res.data.collection;

            if (selectedItem && !this.lstModels.some((m) => m.id === selectedId)) {
              this.lstModels.push(selectedItem);
            }

            this.changeDetectorRef.detectChanges();
          }
          this.modelLoading = false;
          if (callback) {
            callback();
          }
        },
        (err) => {
          this.modelLoading = false;
          if (callback) {
            callback();
          }
        }
      );
  }

  loadNextModelPage() {
    this.modelLoading = true;
    this.modelPage++;
    this.equipmentModelService
      .apiEquipmentModelsByBrandGet(this.selectedBrandId, this.modelSearchCtrl.value, this.modelPage, this.modelPageSize)
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.modelTotalPages = Math.ceil(res.data.total / this.modelPageSize);
            const newModels: any[] = res.data.collection;
            newModels.forEach((newM: any) => {
              if (!this.lstModels.some((m) => m.id === newM.id)) {
                this.lstModels.push(newM);
              }
            });
            this.lstModels = this.lstModels.slice();
            this.changeDetectorRef.detectChanges();
          }
          this.modelLoading = false;
        },
        (err) => {
          this.modelLoading = false;
        }
      );
  }

  onModelSelectOpened() {
    setTimeout(() => {
      if (this.modelSelect && this.modelSelect.panel && this.modelSelect.panel.nativeElement) {
        const panel = this.modelSelect.panel.nativeElement;
        panel.removeEventListener('scroll', this.onModelScrollBound);
        panel.addEventListener('scroll', this.onModelScrollBound);
      }
    }, 100);
  }

  onModelSelectClosed() {
    if (this.modelSearchCtrl.value) {
      this.modelSearchCtrl.setValue('');
      this.loadModels();
    }
  }

  onModelScroll(event: any) {
    const target = event.target;
    const threshold = 600;
    const nearBottom = target.scrollHeight - target.clientHeight - target.scrollTop <= threshold;
    if (nearBottom && !this.modelLoading && this.modelPage < this.modelTotalPages) {
      this.loadNextModelPage();
    }
  }

  getById(id: string | number) {
    this.partPackageService.apiPartPackagesIdGet(String(id)).subscribe(
      (res) => {
        const data = (res && res.success && res.data) ? res.data : (res && res.data ? res.data : res);
        if (data) {
          const packageData = data.package || data;
          // Set selectedBrandId trước để tránh valueChanges brand_id load lại model
          this.selectedBrandId = packageData.brand_id || '';
          this.packageForm.patchValue({
            equipment_model_id: packageData.equipment_model_id,
            prefix_ids: [],
            brand_id: packageData.brand_id || '',
            name: packageData.name,
            description: packageData.description,
            serial: packageData.serial || '',
            is_active: packageData.is_active,
            sort_order: packageData.sort_order || 0,
            default_image: packageData.default_image || '',
          }, { emitEvent: false });

          // Đảm bảo model đã chọn có trong lstModels (dùng extend_equipment_model nếu có)
          if (packageData.extend_equipment_model) {
            const modelExists = this.lstModels.some((m) => m.id === packageData.equipment_model_id);
            if (!modelExists) {
              this.lstModels.push(packageData.extend_equipment_model);
            }
          }

          this.currentImage = packageData.default_image || null;
          const existingPrefixIds: string[] = (packageData.prefixes || []).map((p: any) => p.id);
          this.loadPrefixesByModel(packageData.equipment_model_id, existingPrefixIds);
          // Reload models theo brand đã chọn (giữ lại model đang chọn)
          this.loadModels();
          this.changeDetectorRef.detectChanges();
        }
      },
      (err) => {
        console.error('Error loading part package details:', err);
      }
    );
  }

  onFileSelected(event: any) {
    const file: File = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.previewUrl = reader.result as string;
      this.changeDetectorRef.detectChanges();
    };
    reader.readAsDataURL(file);

    // Khi sửa: có id rồi nên upload ngay
    if (this.isEdit && this.data.id != null) {
      this.uploadImage(String(this.data.id), file);
    }
  }

  removeImage() {
    this.selectedFile = null;
    this.previewUrl = null;
    this.currentImage = null;
    this.packageForm.patchValue({ default_image: '' });
  }

  uploadImage(packageId: string, file: File) {
    this.isUploading = true;
    this.partPackageService.apiPartPackagesIdImagePut(packageId, file).subscribe(
      (res) => {
        this.isUploading = false;
        if (res && res.success) {
          const link = res.data && (res.data.default_image || res.data.link || res.data.path) || res.link || res.path;
          if (link) {
            this.packageForm.patchValue({ default_image: link });
            this.currentImage = link;
            this.previewUrl = null;
          }
          this.toastr.success('Tải ảnh thành công');
        } else {
          this.toastr.error(res && res.message ? res.message : 'Tải ảnh thất bại');
        }
        this.changeDetectorRef.detectChanges();
      },
      (err) => {
        this.isUploading = false;
        this.toastr.error('Tải ảnh thất bại');
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  save() {
    this.hasFormErrors = true;
    const controls = this.packageForm.controls;
    if (this.packageForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    const payload = this.packageForm.value;

    if (!this.isEdit) {
      this.partPackageService.apiPartPackagesPost(payload).subscribe((res) => {
        if (res.success) {
          const newId = res.data && res.data.id ? String(res.data.id) : null;
          // Nếu có ảnh đang chờ upload thì upload sau khi tạo xong
          if (newId && this.selectedFile) {
            this.partPackageService.apiPartPackagesIdImagePut(newId, this.selectedFile).subscribe(
              () => {
                this.dialogRef.close({ event: true, type: 'create' });
              },
              () => {
                this.toastr.warning('Tạo gói thành công nhưng tải ảnh thất bại');
                this.dialogRef.close({ event: true, type: 'create' });
              }
            );
          } else {
            this.dialogRef.close({ event: true, type: 'create' });
          }
        } else {
          this.dialogRef.close({ event: false, message: res.message });
        }
      });
    } else {
      this.partPackageService.apiPartPackagesIdPut(String(this.data.id), payload).subscribe((res) => {
        if (res.success) {
          // Ảnh đã upload ngay khi chọn, chỉ cần đóng
          this.dialogRef.close({ event: true, type: 'edit' });
        } else {
          this.dialogRef.close({ event: false, message: res.message });
        }
      });
    }
  }

  closeModal() {
    this.dialogRef.close();
  }
}
